import { CartSpecificationLine, RiserPan } from "@/app/models/cart/types";
import dataService from "@/app/services/dataService";
import { useEffect, useState } from "react";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";

export type LineItem = {
  id: number | "";
  qty: number | "";
  lengthFt: number | "";
  lengthIn: number | "";
};

export type BoxSelectorData = {
  boxType: string;
  ribType: string;
  lines: LineItem[];
};

type Props = {
  cartItemId: number;
  initialValue?: BoxSelectorData;
  onChange: (data: BoxSelectorData) => void;
  cartLineRiserPan: boolean;
};

const EMPTY_LINE: LineItem = { id: "", qty: "", lengthFt: "", lengthIn: "" };

export default function CartSpecificationLines({ cartItemId, 
  initialValue, 
  onChange, 
  cartLineRiserPan }: Props) {
  const [loading, setLoading] = useState(false);
  const [boxType, setBoxType] = useState<string>(initialValue?.boxType || (cartLineRiserPan ? 'BOX/SQ' : ''));
  const [ribType, setRibType] = useState<string>(initialValue?.ribType || (cartLineRiserPan ? '5-RIB': ''));
  const [lines, setLines] = useState<LineItem[]>(
    (initialValue?.lines?.length
      ? initialValue.lines.map(toLineItem)
      : [EMPTY_LINE]) as LineItem[]
  );

  function toLineItem(v: any): LineItem {
    const toNumOrEmpty = (x: any) =>
      x === "" || x === null || x === undefined || Number.isNaN(Number(x))
        ? ""
        : Number(x);

    return {
      id: toNumOrEmpty(v?.id),
      qty: toNumOrEmpty(v?.qty),
      lengthFt: toNumOrEmpty(v?.lengthFt),
      lengthIn: toNumOrEmpty(v?.lengthIn),
    };
  }

  const toNumOrZero = (x: any) =>
    x === "" || x === null || x === undefined || Number.isNaN(Number(x)) ? 0 : Number(x);

  const isCompleteLine = (l: LineItem) =>
    l.lengthFt !== "" && l.lengthIn !== "" && l.qty !== "";

  useEffect(() => {
    if (!initialValue) return;

    const nextBox = initialValue.boxType || (cartLineRiserPan ? 'BOX/SQ' : '');
    const nextRib = initialValue.ribType || (cartLineRiserPan ? '5-RIB': '');
    const nextLines: LineItem[] = initialValue.lines?.length ? initialValue.lines.map(toLineItem) : [EMPTY_LINE];

    setLines(nextLines);

    const boxSame = nextBox === boxType;
    const ribSame = nextRib === ribType;

    if (!boxSame || !ribSame) {
      setBoxType(nextBox);
      setRibType(nextRib);
    }
  }, [initialValue]);

  const handleLineChange = async (index: number, field: keyof LineItem, value: string) => {
    try {
      setLoading(true);
      const normalized = value === "" ? "" : Number(value);
      const nextLines = lines.map((l, i) =>
        i === index ? { ...l, [field]: normalized } : l
      );

      setLines(nextLines);

      const nextLine = nextLines[index];

      if (isCompleteLine(nextLine)) {
        const items: CartSpecificationLine[] = [
          {
            id: toNumOrZero(nextLine.id),
            displayOrder: index,
            lengthFt: toNumOrZero(nextLine.lengthFt),
            lengthIn: toNumOrZero(nextLine.lengthIn),
            qty: toNumOrZero(nextLine.qty),
          },
        ];

        const response = await dataService.addCartSpecificationLines(cartItemId, items);
        if (response?.success) {
          onChange({ boxType, ribType, lines: nextLines });
        }
      }
    } catch (error) {
      console.log(error);
    }
    finally {
      setLoading(false);
    }
  };


  const handleAddLine = () => {
    setLines((prev) => [...prev, { ...EMPTY_LINE }]);
  };

  const handleRemoveLine = async (index: number) => {
    try {
      setLoading(true);
      const currentLine = lines[index];
      const nextLines = lines.filter((_, i) => i !== index);
      setLines(nextLines);

      if (currentLine.id) {
        const response = await dataService.deleteCartSpecificationLine(currentLine.id);
        if (response?.success) {
          onChange({ boxType, ribType, lines: nextLines });
        }
      }
    } catch (error) {
      console.log(error);
    }
    finally {
      setLoading(false);
    }
  };

  const handleSetRiserPan = async (type: "box" | "rib", val: string) => {
    if (type === "box") setBoxType(val);
    if (type === "rib") setRibType(val);

    try {
      setLoading(true);

      const item: RiserPan = {
        box: type === "box" ? val : boxType,
        rib: type === "rib" ? val : ribType,
      };

      const response = await dataService.updateRiserPan(cartItemId, item);
      if (response?.success) {
        onChange({
          boxType: type === "box" ? val : boxType,
          ribType: type === "rib" ? val : ribType,
          lines,
        });
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };


  return (
    <>
      <Backdrop
        sx={{
          color: "#fff",
          zIndex: 1600,
        }}
        open={loading}
      >
        <CircularProgress color="primary" />
      </Backdrop>
      <div className="production-line-container">
        {cartLineRiserPan && (
          <>
            <div className="product-box">
              <label style={{ fontWeight: 600, minWidth: "40px" }}>Box:</label>
              <div className="inp-bx">
                <div className="inp-bx-item">
                  <input
                    type="radio"
                    value="BOX/SQ"
                    checked={boxType === "BOX/SQ"}
                    onChange={(e) => handleSetRiserPan("box", e.target.value)}
                  />
                  <label style={{ fontWeight: 600 }}>BOX/SQ</label>
                </div>
                <div className="inp-bx-item">
                  <input
                    type="radio"
                    value="SQ/SQ"
                    checked={boxType === "SQ/SQ"}
                    onChange={(e) => handleSetRiserPan("box", e.target.value)}
                  />
                  <label style={{ fontWeight: 600 }}>SQ/SQ</label>
                </div>
                <div className="inp-bx-item">
                  <input
                    type="radio"
                    value="BOX/TT"
                    checked={boxType === "BOX/TT"}
                    onChange={(e) => handleSetRiserPan("box", e.target.value)}
                  />
                  <label style={{ fontWeight: 600 }}>BOX/TT</label>
                </div>
              </div>
            </div>

            <div className="product-rib">
              <label style={{ fontWeight: 600, minWidth: "40px" }}>RIB:</label>
              <div className="inp-bx">
                <div className="inp-bx-item">
                  <input
                    type="radio"
                    value="2-RIB"
                    checked={ribType === "2-RIB"}
                    onChange={(e) => handleSetRiserPan("rib", e.target.value)}
                  />
                  <label style={{ fontWeight: 600 }}>2-RIB</label>
                </div>
                <div className="inp-bx-item">
                  <input
                    type="radio"
                    value="5-RIB"
                    checked={ribType === "5-RIB"}
                    onChange={(e) => handleSetRiserPan("rib", e.target.value)}
                  />
                  <label style={{ fontWeight: 600 }}>5-RIB</label>
                </div>
              </div>
            </div>
          </>
        )}
        
        <div>
          {lines.map((line, index) => (
            <div
              key={index}
              style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "12px" }}
            >
              <div className="production-line-input-container">
                <label>Qty</label>
                <input
                  className="production-line-input"
                  type="number"
                  value={line.qty}
                  onChange={(e) => handleLineChange(index, "qty", e.target.value)}
                />
              </div>

              <div className="production-line-input-container">
                <label>Ft</label>
                <input
                  className="production-line-input"
                  type="number"
                  value={line.lengthFt}
                  onChange={(e) => handleLineChange(index, "lengthFt", e.target.value)}
                />
              </div>

              <div className="production-line-input-container">
                <label>In</label>
                <input
                  className="production-line-input"
                  type="number"
                  value={line.lengthIn}
                  onChange={(e) => handleLineChange(index, "lengthIn", e.target.value)}
                />
              </div>

              <div className="production-line-input-container" style={{ marginTop: 18 }}>
                {index > 0 && (
                  <button onClick={() => handleRemoveLine(index)} className="trash-button" aria-label="Remove line">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" width="18" height="18">
                      <path
                        fill="#444"
                        d="M 15 4 C 14.476563 4 13.941406 4.183594 13.5625 4.5625 C 13.183594 4.941406 13 5.476563 13 6 L 13 7 L 7 7 L 7 9 L 8 9 L 8 25 C 8 26.644531 9.355469 28 11 28 L 23 28 C 24.644531 28 26 26.644531 26 25 L 26 9 L 27 9 L 27 7 L 21 7 L 21 6 C 21 5.476563 20.816406 4.941406 20.4375 4.5625 C 20.058594 4.183594 19.523438 4 19 4 Z M 15 6 L 19 6 L 19 7 L 15 7 Z M 10 9 L 24 9 L 24 25 C 24 25.554688 23.554688 26 23 26 L 11 26 C 10.445313 26 10 25.554688 10 25 Z M 12 12 L 12 23 L 14 23 L 14 12 Z M 16 12 L 16 23 L 18 23 L 18 12 Z M 20 12 L 20 23 L 22 23 L 22 12 Z"
                      />
                    </svg>
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        <button className="blueButtonOutline" onClick={handleAddLine}>
          + ADD LINE
        </button>
      </div>
    </>
  );
}
